import { supabase } from './supabase';
import usageService from './usageService';

class AIService {
  constructor() {
    this.initialized = true; // Always available through Edge Function
  }

  // Check if AI service is available
  isAvailable() {
    return true; // Always available as it's server-side
  }

  // Improve review text using AI via Edge Function
  async improveReview(originalReview, serviceType = 'service', businessName = 'ServiceFlow Pro', organizationId = null, isPublicSubmission = false) {
    try {
      if (!originalReview || originalReview.trim().length === 0) {
        return { success: false, error: 'Please provide some feedback to improve' };
      }

      // For public review submissions, skip authentication check
      if (!isPublicSubmission) {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          return { success: false, error: 'Authentication required' };
        }
      }

      // Check usage limits if organizationId is provided
      if (organizationId) {
        const canGenerateResult = await usageService.canGenerateAIReview(organizationId);
        
        if (!canGenerateResult.success) {
          return { success: false, error: canGenerateResult.error };
        }
        
        if (!canGenerateResult.canGenerate) {
          return { 
            success: false, 
            error: canGenerateResult.reason || 'Monthly AI review limit reached',
            code: 'USAGE_LIMIT_REACHED',
            usage: canGenerateResult.usage
          };
        }
      }

      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke('improve-review', {
        body: {
          original_review: originalReview,
          service_type: serviceType,
          business_name: businessName
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        return { success: false, error: error.message || 'AI service error' };
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Failed to improve review' };
      }

      // Increment usage counter if organizationId is provided and AI generation was successful
      if (organizationId) {
        const incrementResult = await usageService.incrementAIReviewUsage(organizationId);
        
        if (!incrementResult.success) {
          console.warn('Failed to increment usage counter:', incrementResult.error);
          // Don't fail the entire operation, just log the warning
        }
      }

      return { 
        success: true, 
        data: data.data
      };
    } catch (error) {
      console.error('Error improving review:', error);
      return { success: false, error: 'Failed to improve review. Please try again.' };
    }
  }

  // Generate text using AI via Edge Function
  async generateText(prompt) {
    try {
      if (!prompt || prompt.trim().length === 0) {
        return { success: false, error: 'Please provide a prompt' };
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        return { success: false, error: 'Authentication required' };
      }

      // Call the Edge Function with a general prompt
      const { data, error } = await supabase.functions.invoke('improve-review', {
        body: {
          original_review: prompt,
          service_type: 'general',
          business_name: 'ServiceFlow Pro',
          mode: 'generate' // Special mode for text generation
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        return { success: false, error: error.message || 'AI service error' };
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Failed to generate text' };
      }

      return { 
        success: true, 
        data: data.data
      };
    } catch (error) {
      console.error('Error generating text:', error);
      return { success: false, error: 'Failed to generate text. Please try again.' };
    }
  }

  // Generate review suggestions based on rating (simplified version)
  async generateReviewSuggestions(rating, serviceType = 'service') {
    try {
      // For now, return static suggestions based on rating
      // This could be enhanced to use AI later if needed
      const suggestions = this.getStaticSuggestions(rating, serviceType);
      
      return { 
        success: true, 
        data: suggestions
      };
    } catch (error) {
      console.error('Error generating suggestions:', error);
      return { success: false, error: 'Failed to generate suggestions' };
    }
  }

  // Static suggestions as fallback
  getStaticSuggestions(rating, serviceType) {
    const suggestions = {
      1: [
        "The service didn't meet my expectations and I was disappointed with the experience.",
        "Unfortunately, I had several issues with the service quality and communication.",
        "The service fell short of what was promised and needs significant improvement."
      ],
      2: [
        "The service was below average and had room for improvement in several areas.",
        "While the team was polite, the service quality didn't meet my expectations.",
        "There were some issues with the service that affected my overall satisfaction."
      ],
      3: [
        "The service was adequate and met basic expectations.",
        "It was an average experience - nothing exceptional but got the job done.",
        "The service was okay, though there's room for improvement in some areas."
      ],
      4: [
        "I was pleased with the service and would recommend it to others.",
        "The team was professional and the service quality was very good.",
        "Great service overall with only minor areas for improvement."
      ],
      5: [
        "Outstanding service! The team exceeded my expectations in every way.",
        "Exceptional quality and professionalism. Highly recommend to anyone!",
        "Perfect service from start to finish. Couldn't ask for better!"
      ]
    };

    return suggestions[rating] || suggestions[3];
  }

  // Legacy methods for backward compatibility (no longer needed)
  initialize() {
    return { success: true };
  }

  getApiKey() {
    return null; // Not needed for server-side implementation
  }

  saveApiKey() {
    return { success: true }; // Not needed for server-side implementation
  }

  autoInitialize() {
    return { success: true };
  }
}

// Export singleton instance
export const aiService = new AIService();
export default aiService; 